import React from 'react'
import { Check, X } from 'react-feather'
import styled, { useTheme } from 'styled-components'
import { Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ProposalParams, ProposalTally } from '@mprolab/sdk'

const Wrapper = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.backgroundContrast};
`

const Track = styled.div`
  height: 12px;
  border-radius: 6px;
  width: 100%;
  background: ${({ theme }) => theme.colors.gray8};
  display: grid;
  grid-template-columns: var(--yes-pct) var(--no-pct) var(--abstain-pct);
  overflow: hidden;
`

const Bar = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  height: 100%;
`

const Legend = styled(Flex)`
  flex-wrap: wrap;
  gap: 18px;
  align-items: center;
`

const Dot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
`

interface TallyBarProps {
  tally: ProposalTally
  params: ProposalParams
}

const TallyBar: React.FC<TallyBarProps> = ({ tally, params }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const total =
    tally.yes + tally.no + tally.abstain || 1
  const yesPct = (tally.yes / total) * 100
  const noPct = (tally.no / total) * 100
  const abstainPct = (tally.abstain / total) * 100
  const quorum = Number.isFinite(tally.quorumPercent)
    ? tally.quorumPercent.toFixed(2)
    : '0.00'
  /*
   * A proposal opened before scope 11 has no floors and is still judged by the
   * percentage quorum it was opened under. The screen shows whichever rule
   * actually governs it, rather than the rule in force today.
   */
  const usesFloors = Boolean(tally.floors?.minDistinctVoters)
  const boosts = tally.clanBoosts ?? []

  return (
    <Wrapper>
      <Flex justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap="8px">
        <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('dao.currentTally')}</Text>
        <Flex gap="16px">
          {/*
            Participation is two floors now, not one percentage — voters first,
            because a headcount is the part somebody can do something about.
            The rule itself lives behind the "i".
          */}
          {usesFloors ? (
            <Flex gap="12px" alignItems="center" flexWrap="wrap">
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('dao.voters')}: {tally.distinctVoters ?? 0} /{' '}
                {tally.floors?.minDistinctVoters}{' '}
                {tally.floors?.distinctVotersMet ? <Check size={14} /> : <X size={14} />}
              </Text>
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('dao.weight')}: {quorum}% / {tally.floors?.minVotedVpPercent}%{' '}
                {tally.floors?.votedVpMet ? <Check size={14} /> : <X size={14} />}
              </Text>
              <InfoIcon
                iconProps={{ size: 14 }}
                tooltipProps={{ title: t('dao.floorsTooltip') }}
              />
            </Flex>
          ) : (
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('dao.quorum')}: {quorum}% / {params.quorumPercent}%{' '}
              {tally.quorumReached ? <Check size={14} /> : <X size={14} />}
              <InfoIcon
                iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
                tooltipProps={{ title: t('dao.legacyQuorumTooltip') }}
              />
            </Text>
          )}
          <Text
            variant={TextVariant.BODY_SMALL_BOLD}
            color={tally.passing ? colors.green : colors.red}
          >
            {tally.passing ? t('dao.passing') : t('dao.notPassing')}
          </Text>
        </Flex>
      </Flex>
      <Track
        style={
          {
            '--yes-pct': `${yesPct}%`,
            '--no-pct': `${noPct}%`,
            '--abstain-pct': `${abstainPct}%`
          } as React.CSSProperties
        }
      >
        <Bar $color={colors.green} />
        <Bar $color={colors.red} />
        <Bar $color={colors.gray6} />
      </Track>
      <Legend>
        <Flex alignItems="center">
          <Dot $color={colors.green} />
          <Text variant={TextVariant.BODY_SMALL}>
            {t('dao.choice.yes')}: {tally.yes.toLocaleString()} VP ({yesPct.toFixed(1)}%)
          </Text>
        </Flex>
        <Flex alignItems="center">
          <Dot $color={colors.red} />
          <Text variant={TextVariant.BODY_SMALL}>
            {t('dao.choice.no')}: {tally.no.toLocaleString()} VP ({noPct.toFixed(1)}%)
          </Text>
        </Flex>
        <Flex alignItems="center">
          <Dot $color={colors.gray6} />
          <Text variant={TextVariant.BODY_SMALL}>
            {t('dao.choice.abstain')}: {tally.abstain.toLocaleString()} VP ({abstainPct.toFixed(1)}%)
          </Text>
        </Flex>
      </Legend>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('dao.tallyRule')}
      </Text>

        {boosts.length > 0 && (
          <Flex flexDirection="column" gap="4px" mt="8px">
            {boosts.map((b) => (
              <Text key={b.clanId} variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {b.name ?? t('dao.aClan')} · +{b.addedVP.toLocaleString()} → {b.choice} ({b.boostedWallets})
              </Text>
            ))}
            <InfoIcon iconProps={{ size: 14 }} tooltipProps={{ title: t('dao.boostTooltip') }} />
          </Flex>
        )}
    </Wrapper>
  )
}

export default TallyBar
